import { useContext, useState, useMemo, useCallback } from 'react';
import styled from '@emotion/styled';
import { useFloating, useInteractions, useClick, useDismiss } from '@floating-ui/react';
import type { QuantAnalysisItem } from '@trinity/chat/api/interfaces/projects.quant-analysis';
import { QuantAnalysisFilterContext } from '@trinity/chat-components';
import { cx } from '@trinity/chat-components';
import { AnalysisTable } from '@trinity/chat-components/components/QuantAnalysis/Analysis.Table.js';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context.js';
import { BrandingProvider } from '@trinity/chat-components/containers/Branding/BrandingContainer.js';
import { filterTableData, applyCustomNamesToFilteredData } from '@trinity/chat-components/utils/quant-analysis.js';
import { ChevronRight } from 'react-feather';
import { AnalysisContextMenu as ContextMenu } from './Analysis.ContextMenu';
import { AnalysisFilterMenu } from './Analysis.FilterMenu';
import { QuantAnalysisFilterButton as FilterButton } from './FilterButton';
import { QuantAttributeTabButton } from './QuantAttributeTabButton';
import { QuantQuickChart } from './QuickChart';
import styles from './style/Analysis.Content.module.css';

type Props = {
  className?: string;
  value: QuantAnalysisItem;
};

export const AnalysisContent = (props: Props) => {
  const filterColumnNames = useMemo(() => {
    return Array.from(new Set(props.value.data.columnNames.map(col => col[0])));
  }, [props.value.data.columnNames]);
  const filterRowNames = useMemo(() => {
    return Array.from(new Set(props.value.data.rowNames.map(row => row[0])));
  }, [props.value.data.rowNames]);
  const [quantFilter, setQuantFilter] = useContext(QuantAnalysisFilterContext);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [showAllButtons, setShowAllButtons] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open: filterMenuOpen,
    onOpenChange: setFilterMenuOpen,
    placement: 'bottom-start',
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);
  const theming = useContext(AppThemingContext);

  const enabled = theming?.theme.enabled;
  const color = enabled ? theming?.palette?.primary?.main : '#28AAE1';

  const selectOptions = useMemo(() => {
    return [
      ...filterColumnNames.map(name => ({ value: name, label: name })),
      ...filterRowNames.map(name => ({ value: name, label: name })),
    ];
  }, [filterColumnNames, filterRowNames]);

  const handleFilterColumnChanges = useCallback((colName: string) => {
    setQuantFilter(prev => {
      const newColumnNames = prev.columnNames.includes(colName)
        ? prev.columnNames.filter(name => name !== colName)
        : [...prev.columnNames, colName];
      return { ...prev, columnNames: newColumnNames };
    });
  }, [setQuantFilter]);

  const handleFilterRowChanges = useCallback((rowName: string) => {
    setQuantFilter(prev => {
      const newRowNames = prev.rowNames.includes(rowName)
        ? prev.rowNames.filter(name => name !== rowName)
        : [...prev.rowNames, rowName];
      return { ...prev, rowNames: newRowNames };
    });
  }, [setQuantFilter]);

  const columnButtons = useMemo(() => (
    filterColumnNames.map(colName => (
      <QuantAttributeTabButton
        key={colName}
        name={colName}
        selected={quantFilter.columnNames.includes(colName)}
        onClick={() => handleFilterColumnChanges(colName)} />
    ))
  ), [filterColumnNames, handleFilterColumnChanges, quantFilter.columnNames]);

  const rowButtons = useMemo(() => (
    filterRowNames.map(rowName => (
      <QuantAttributeTabButton
        key={rowName}
        name={rowName}
        selected={quantFilter.rowNames.includes(rowName)}
        onClick={() => handleFilterRowChanges(rowName)} />
    ))
  ), [filterRowNames, handleFilterRowChanges, quantFilter.rowNames]);

  const allButtons = useMemo(() => [...columnButtons, ...rowButtons], [columnButtons, rowButtons]);
  const maxVisibleButtons = 7;

  const shouldShowToggle = allButtons.length > maxVisibleButtons;
  const visibleButtons = showAllButtons ? allButtons : allButtons.slice(0, maxVisibleButtons);

  const handleToggleButtons = useCallback(() => {
    setShowAllButtons(prev => !prev);
  }, []);

  return (
    <div className={cx(styles.root, props.className)}>
      <div className={styles.header}>
        <div className={styles.title}>
          {props.value.name}
        </div>
        <div className={styles.actions}>
          <ResetButton
            color={color}
            onClick={() => setQuantFilter({
              columnNames: [], rowNames: [], sortConfig: null, selection: {
                rows: new Set(),
                columns: new Set(),
                cells: new Map(),
              },
            })}>
            Reset
          </ResetButton>
          <FilterButtonContainer ref={refs.setReference} {...getReferenceProps()}>
            <FilterButton
              onClick={() => setFilterMenuOpen(open => !open)}
              color={color} />
          </FilterButtonContainer>
          <ContextMenu analysisItem={props.value} />
        </div>
        {filterMenuOpen && (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}>
            <AnalysisFilterMenu
              columnNames={filterColumnNames}
              rowNames={filterRowNames}
              selectOptions={selectOptions}
              analysisName={props.value.name} />
          </div>
        )}
      </div>

      <ButtonContainer>
        {visibleButtons}
        {shouldShowToggle && (
          <IndicatorIcon
            isExpanded={showAllButtons}
            onClick={handleToggleButtons}
            color={color} />
        )}
      </ButtonContainer>

      <BrandingProvider enabled={enabled}>
        <QuantQuickChart />
      </BrandingProvider>

      <AnalysisTable
        className={styles.table}
        data={(() => {
          const dataWithCustomNames = applyCustomNamesToFilteredData(
            props.value.data,
            props.value.data,
            quantFilter.customColumnNames,
            quantFilter.customRowNames
          );
          return filterTableData(dataWithCustomNames, quantFilter.columnNames, quantFilter.rowNames);
        })()}
        originalData={props.value.data} />

      <div className={styles.footer}>
        {props.value.footer.map(f => (
          <div key={f} className={styles['footer-item']}>{f}</div>
        ))}
      </div>
    </div>
  );
};

const IndicatorIcon = ({
  size = 24,
  isExpanded = false,
  onClick,
  color,
}: {
  size?: number;
  isExpanded?: boolean;
  onClick?: () => void;
  color?: string;
}) => {
  return (
    <CardLink onClick={onClick} $isExpanded={isExpanded}>
      <ToggleText color={color}>{isExpanded ? 'Show less' : 'Show more'}</ToggleText>
      <StyledChevronRightIcon
        size={size} />
    </CardLink>
  );
};

const ButtonContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 16px;
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
  gap: 4px;
`;

const ResetButton = styled.button<{ color: string }>(({ color }) => ({
  background: '#fff',
  color,
  fontWeight: 500,
  fontSize: '1rem',
  cursor: 'pointer',
  marginRight: '4px',
  border: 'none',
  padding: '4px 8px',
  borderRadius: '4px',
}));

const FilterButtonContainer = styled.span`
  display: flex;
  align-items: center;
`;

const StyledChevronRightIcon = styled(ChevronRight)<{ $isExpanded?: boolean }>(({ theme }) => ({
  color: theme.palette.sentiment.primary.main,
  paddingLeft: 2,
  strokeWidth: 3,
}));

const ToggleText = styled.span<{ color: string }>(({ color }) => ({
  fontSize: '0.875rem',
  color,
  marginRight: '4px',
  fontWeight: 500,
}));

const CardLink = styled.div<{ $isExpanded?: boolean }>(({ $isExpanded }) => ({
  borderRadius: '6px',
  padding: '8px 12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    '& svg': {
      transform: $isExpanded ? 'rotate(0deg)' : 'rotate(90deg)',
    },
  },
}));
